import { IDimensionPoint } from '@/editor/data/dimension/dimension';
import { EColumnType, IColumnData } from '@/editor/data/model/model';
import { getPureBoxesByData } from '@/editor/data/utils';
import AreaDimensionTransformEntity from '@/editor/entities/areaDimensionTransformEntity';
import TransformEntity from '@/editor/entities/transformEntity';
import { EEnableScaleMode, ITransformEntityParams } from '@/editor/entities/type';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { globalStores } from '@/editor/stores';
import { EEntityType, EOperateMode, ESceneMode, ISelected } from '@/editor/types';
import { isMultiSelectFurnitureOrAssemblyOnly } from '@/editor/utils/select/multi-select';

export class TransformManager {
  private _transformEntity: TransformEntity;
  private _areaTransformEntity: AreaDimensionTransformEntity;
  private _prevZoom = globalStores.scene2dZoom;
  private _rafId: number | null = null;
  private _disposers: Set<() => void> = new Set();

  constructor() {
    this._transformEntity = new TransformEntity();
    this._areaTransformEntity = new AreaDimensionTransformEntity();
    this._disposers
      .add(editorEvent.on(EditorEventName.VIEW2D_CHANGE, this.handleView2dChange))
      .add(editorEvent.on(EditorEventName.ENTITY_SELECTED, this.refreshBySelectedEntity))
      .add(editorEvent.on(EditorEventName.SCENE_MODE_CHANGE, this.refreshBySelectedEntity))
      .add(editorEvent.on(EditorEventName.OPERATE_MODE_CHANGE, this.refreshBySelectedEntity))
      .add(editorEvent.on(EditorEventName.KEYBOARD_PRESS_UPDATE, this.refreshBySelectedEntity))
      .add(globalStores.editorStateStore.onChange('moving', this._refreshByMoving));
  }

  // 主要用于选中框显示判断
  get visible() {
    return this._transformEntity.created;
  }

  private _refreshByMoving = (isMoving: boolean) => {
    if (isMoving) {
      this._setVisible(false);
      return;
    }

    // 拖拽结束后实体几何可能已变化（如弧形墙上的门窗会按 distance 重新投影到弧线上），
    // 拖拽过程中变换控件已是 created 状态，若走 _setVisible(true) 会因 visible 未变而提前 return，
    // 导致控制柄停留在拖拽时的位置、不跟随实体。这里强制按最新数据重建。
    this.refreshBySelectedEntity();
  };

  private _cancelRaf() {
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  refreshBySelectedEntity = () => {
    const { selected, moving } = globalStores.editorStateStore;
    const currentSceneMode = globalStores.sceneMode;
    const operateInfo = globalStores.operateInfo;

    if (currentSceneMode !== ESceneMode.scene2d) {
      this._clearEntity();
      return;
    }

    if (operateInfo.mode !== EOperateMode.EMPTY || selected.type === EEntityType.EMPTY || moving) {
      this._clearEntity();
      return;
    }

    // 进入面积编辑
    if (selected.type === EEntityType.AreaDimension && !moving) {
      this._clearEntity();
      this.refreshAreaDimensionTransformer(selected);
      return;
    }

    if (selected.type === EEntityType.MultiModel) {
      const multiModelData = globalStores.getDataByIdType('', EEntityType.MultiModel);
      if (!isMultiSelectFurnitureOrAssemblyOnly(multiModelData)) {
        this._clearEntity();
        return;
      }
    }

    const data = globalStores.getDataByIdType(selected.entityId, selected.type);
    if (!data) {
      this._clearEntity();
      return;
    }

    const boxesData = getPureBoxesByData(selected.type, data);
    if (!boxesData) {
      this._clearEntity();
      return;
    }

    const params: Partial<ITransformEntityParams> = {
      boxes: boxesData.boxes,
      center: { x: boxesData.x, y: boxesData.y },
      width: boxesData.boxes[boxesData.boxes.length - 1].width,
      height: boxesData.boxes[boxesData.boxes.length - 1].thickness,
      rotation: boxesData.rotation,
      enableRotation: false,
      enableScaleMode: EEnableScaleMode.Empty
    };
    switch (selected.type) {
      case EEntityType.Column: {
        const columnData = data as IColumnData;
        if (columnData.type === EColumnType.cylinder) {
          params.enableRotation = false;
          params.enableScaleMode = EEnableScaleMode.Corner;
        } else {
          params.enableRotation = true;
          params.enableScaleMode = EEnableScaleMode.All;
        }
        break;
      }
      case EEntityType.Furniture: {
        params.enableRotation = true;
        params.enableScaleMode = EEnableScaleMode.All;
        break;
      }
      case EEntityType.DoorWindow: {
        params.enableRotation = false;
        params.enableScaleMode = EEnableScaleMode.LeftRight;
        break;
      }
      case EEntityType.Assembly: {
        params.enableRotation = true;
        params.enableScaleMode = EEnableScaleMode.Empty;
        break;
      }
      case EEntityType.MultiModel: {
        params.enableRotation = true;
        params.enableScaleMode = EEnableScaleMode.Empty;
        break;
      }
    }

    this._cancelRaf();

    this._rafId = requestAnimationFrame(() => {
      this.update({ ...params, forceUpdate: true });
      this._rafId = null;
    });
  };

  private _clearEntity() {
    this._cancelRaf();
    this._transformEntity.clear();
    this._areaTransformEntity.clear();
  }

  private handleView2dChange = () => {
    if (!this.visible) return;
    if (this._prevZoom === globalStores.scene2dZoom) return;

    this._prevZoom = globalStores.scene2dZoom;
    this.update({ forceUpdate: true });
  };

  private _setVisible(visible: boolean) {
    if (this.visible === visible) return;
    if (visible) {
      this.refreshBySelectedEntity();
    } else {
      this._clearEntity();
    }
  }

  getTransformEntity() {
    return this._transformEntity;
  }

  getData() {
    return this._transformEntity.getData();
  }

  update(params: Partial<ITransformEntityParams>) {
    this._transformEntity.update(params);
  }

  // ========== 面积标注相关 ========== //
  getAreaTransformEntity() {
    return this._areaTransformEntity;
  }

  getAreaTransformData() {
    return this._areaTransformEntity.getData();
  }

  refreshAreaDimensionTransformer(selected: ISelected) {
    if (selected.type !== EEntityType.AreaDimension) {
      this._clearEntity();
      return;
    }
    const data = globalStores.dimensionStore.getAreaDim(selected.entityId);

    if (!data) {
      this._clearEntity();
      return;
    }

    this._areaTransformEntity.update(data.points);
  }

  updateAreaTransform(points: IDimensionPoint[]) {
    this._areaTransformEntity.update(points);
  }

  dispose() {
    this._disposers.forEach((dispose) => dispose());
    this._disposers.clear();
  }
}
